import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { callFunction } from '../../firebase/functions';
import storage from '../../firebase/storage';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { ChatBubbleLeftRightIcon, XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';

const inputCls = 'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const MAX_SCREENSHOT_BYTES = 20 * 1024 * 1024;

const URGENCY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'neededSoon', label: 'Needed soon' },
  { value: 'roadmap', label: 'Roadmap' },
  { value: 'ideaToDiscuss', label: 'Idea to discuss' },
];

function uploadScreenshot(uid, file) {
  return new Promise((resolve, reject) => {
    const path = `feedback/${uid}/${Date.now()}_${file.name}`;
    const task = uploadBytesResumable(storageRef(storage, path), file);
    task.on('state_changed', null, reject, async () => {
      resolve(await getDownloadURL(task.snapshot.ref));
    });
  });
}

// Temporary — a lightweight way for academies to report bugs/feedback while
// testing the portal, without leaving whatever page they're on. Remove this
// component (and its App.jsx mount + the submitFeedback Cloud Function)
// once testing wraps up.
export default function FeedbackButton() {
  const { t } = useTranslation();
  const { user, isSuperAdmin, isAcademyAdmin, isSalesRep, isCoach, feedbackButtonEnabled } = useAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [urgency, setUrgency] = useState('neededSoon');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // Staff-only — guardians/players shouldn't see a floating "send feedback"
  // bubble that emails the developer directly (see the temporary-component
  // note above). Super admin (not tied to one academy) always sees it;
  // academy-scoped staff only see it when IAP has switched it on for their
  // specific academy (see AuthContext's feedbackButtonEnabled, toggled from
  // TenantDetail.jsx) — testing academies and ones asked for feedback, not
  // every tenant by default.
  if (!user || !(isSuperAdmin || ((isAcademyAdmin || isSalesRep || isCoach) && feedbackButtonEnabled))) return null;

  const reset = () => {
    setSubject('');
    setUrgency('neededSoon');
    setBody('');
    setFiles([]);
    setError('');
    setSent(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const handleFilesChosen = (e) => {
    const chosen = Array.from(e.target.files || []);
    const tooBig = chosen.find((f) => f.size > MAX_SCREENSHOT_BYTES);
    if (tooBig) {
      setError(t('common:components.feedbackButton.fileTooBig', '"{{name}}" is over the 20MB limit.', { name: tooBig.name }));
      return;
    }
    setError('');
    setFiles((prev) => [...prev, ...chosen]);
    e.target.value = '';
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setError('');
    try {
      const attachmentUrls = [];
      for (const file of files) {
        attachmentUrls.push(await uploadScreenshot(user.uid, file));
      }
      await callFunction('submitFeedback')({ subject: subject.trim(), urgency, body: body.trim(), attachmentUrls });
      setSent(true);
    } catch (err) {
      setError(err.message || t('common:components.feedbackButton.sendFailed', 'Failed to send feedback. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-floating-bubble="feedback"
        onClick={() => setOpen(true)}
        aria-label={t('common:components.feedbackButton.ariaLabel', 'Send feedback')}
        style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 1000,
          width: 48, height: 48, borderRadius: '50%',
          background: '#0f172a', color: '#fff', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)', cursor: 'pointer',
        }}
      >
        <ChatBubbleLeftRightIcon style={{ width: 22, height: 22 }} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1001] flex items-end sm:items-center justify-center bg-black/40 px-4"
          onClick={handleClose}
        >
          <div
            className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">{t('common:components.feedbackButton.title', 'Send feedback')}</h3>
              <button onClick={handleClose}><XMarkIcon className="h-5 w-5 text-gray-400" /></button>
            </div>

            {sent ? (
              <div className="py-6 text-center space-y-3">
                <p className="text-sm text-gray-700">{t('common:components.feedbackButton.sentThanks', 'Thanks — your feedback has been sent.')}</p>
                <button onClick={handleClose} className="text-sm font-semibold text-brand-600">{t('common:actions.close', 'Close')}</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('common:components.feedbackButton.subjectLabel', 'Subject')}</label>
                  <input
                    required autoFocus type="text" value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={t('common:components.feedbackButton.subjectPlaceholder', "What's this about?")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('common:components.feedbackButton.urgencyLabel', 'Urgency')}</label>
                  <select
                    required value={urgency}
                    onChange={(e) => setUrgency(e.target.value)}
                    className={inputCls}
                  >
                    {URGENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(`common:components.feedbackButton.urgency.${opt.value}`, opt.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('common:components.feedbackButton.detailsLabel', 'Details')}</label>
                  <textarea
                    required rows={4} value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder={t('common:components.feedbackButton.detailsPlaceholder', 'What happened, and what did you expect?')}
                    className={inputCls}
                  />
                </div>

                {files.length > 0 && (
                  <div className="space-y-1.5">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                        <span className="text-xs text-gray-600 truncate flex-1">{f.name}</span>
                        <button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-600 flex-shrink-0">
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="flex items-center gap-1.5 text-xs font-semibold text-brand-600 cursor-pointer w-fit">
                  <PaperClipIcon className="h-4 w-4" /> Add screenshots
                  <input type="file" multiple accept="image/*" hidden onChange={handleFilesChosen} />
                </label>

                {error && <p className="text-xs text-red-600">{error}</p>}

                <button
                  type="submit"
                  disabled={sending || !subject.trim() || !body.trim()}
                  className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
